import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api';
import { useResource } from './use-resource';
import type { User } from '../mock/seeds/schema-types';

// Holds the authenticated customer (GET /v1/me) for the whole surface. A single
// place owns the session shape — the app bar shows the avatar/name, and screens read
// the profile from here rather than re-fetching. Duplicated per surface (CB-10).
interface SessionContextValue {
  user: User | undefined;
  loading: boolean;
  error: unknown;
  reload: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, loading, error, reload } = useResource<User>((signal) => api.GET('/me', { signal }), []);
  const value = useMemo<SessionContextValue>(
    () => ({ user: data, loading, error, reload }),
    [data, loading, error, reload],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within a SessionProvider');
  return ctx;
}
